import { Injectable, inject } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { catchError, EMPTY, finalize, Observable, throwError } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Router } from '@angular/router';
import { AlertService } from '../services/alert.service';
import { AlertOptions } from '../data/interfaces/alert';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private _alertService = inject(AlertService);
    private _authService = inject(AuthService);
    private router = inject(Router);


    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        // Clone the request object
        let newReq = req.clone();

        // Request
        //
        // If the access token didn't expire, add the Authorization header.
        // We won't add the Authorization header if the access token expired.
        // This will force the server to return a "401 Unauthorized" response
        // for the protected API routes which our response interceptor will
        // catch and delete the access token from the local storage while logging
        // the user out from the app.
        if (
            this._authService.accessToken &&
            !AuthUtils.isTokenExpired(this._authService.accessToken)
        ) {
            newReq = req.clone({
                headers: req.headers.set(
                    'Authorization',
                    'Bearer ' + this._authService.accessToken
                ),
            });
        } else {
            // console.log('Token is expired');
        }

        // Response
        return next.handle(newReq).pipe(
            catchError((error) => {

                // Catch "401 Unauthorized" responses
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    // Sign out
                    this._authService.signOut().pipe(
                        finalize(() => {
                            const alertOptions: AlertOptions = {
                                keepAfterRouteChange: true
                            }

                            let message = 'You are not authorized to access this page';
                            let route = '/';

                            if (error?.error === "google_reauth_required") {
                                message = 'Google access expired. Please reconnect your Google Ads account.';
                                route = '/login';
                            }
                            
                            this._alertService.error(message, alertOptions);
                            localStorage.removeItem('token');
                            this.router.navigateByUrl(route);
                        })
                    ).subscribe();
                    
                    return EMPTY;                    
                }
                else if (error instanceof HttpErrorResponse && error.status === 403) {
                    this.router.navigateByUrl('/forbidden');
                }
                else if (error instanceof HttpErrorResponse && error.status === 400) {
                    // Replace with proper handling if needed
                }
                else if (error instanceof HttpErrorResponse && error.status === 409) {
                    // Replace with proper handling if needed
                }
                else if (error instanceof HttpErrorResponse && error.status === 0) {
                    // Replace with proper handling if needed
                }

                return throwError(() => error);
            })
        );
    }
}
